import {memo, useEffect, useMemo} from 'react';
import {useWatch} from 'react-hook-form';

import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {useVehicleMake, useVehicleModel} from '@dms/shared';

import {generateVehicleTitle} from '../../utils/generateVehicleTitle';
import {isAutoGeneratedTitle} from '../../utils/isAutoGeneratedTitle';
import {useDebouncedWatch} from '../hooks/useDebouncedWatch';
import {useVehicleFormContext} from '../hooks/useVehicleFormContext';
import {Field} from './primitives/Field';

/**
 * Vehicle title field that automatically generates title from make/model
 * Only auto-generates if the field hasn't been manually touched by the user
 *
 * @example
 * ```tsx
 * <VehicleForm.Section title="Basic Info">
 *   <VehicleForm.Group>
 *     <VehicleForm.MakeSelect />
 *     <VehicleForm.ModelSelect />
 *     <VehicleForm.TitleField />
 *   </VehicleForm.Group>
 * </VehicleForm.Section>
 * ```
 */
function TitleFieldImpl() {
  const {formApi, vehicleType, control} = useVehicleFormContext();

  // Watch make/model immediately (dropdown selections)
  const [formMake, formModelFamily] = useWatch({
    control,
    name: ['formMake', 'formModelFamily'],
  });

  // Debounce trim/variant to reduce performance impact when typing
  const [formTrim, formVariant] = useDebouncedWatch(control, ['trim', 'variant'], 300);

  const [{makeOptions}, {isLoading: isLoadingMakes}] = useVehicleMake(vehicleType);

  const [{modelOptions}, {isLoading: isLoadingModels}] = useVehicleModel(
    vehicleType,
    formMake ? [formMake] : null
  );

  // Wait for catalogue labels before composing the title. Otherwise generateVehicleTitle
  // falls back to raw catalogue codes (e.g. MAKE_JEEP-MODELFAMILY_GRAND_CHEROKEE) when
  // VIN decode fills make/model before make-model options have loaded (T20-131384).
  const isWaitingForCatalogueLabels =
    (isNotNilOrEmpty(formMake) && isLoadingMakes) ||
    (isNotNilOrEmpty(formModelFamily) && isLoadingModels);

  const title = useMemo(() => {
    if (isWaitingForCatalogueLabels) {
      return null;
    }

    return generateVehicleTitle({
      make: formMake,
      model: formModelFamily,
      trim: formTrim,
      variant: formVariant,
      makeOptions,
      modelOptions,
    });
  }, [
    formMake,
    formModelFamily,
    formTrim,
    formVariant,
    makeOptions,
    modelOptions,
    isWaitingForCatalogueLabels,
  ]);

  // Create stable reference for expected initial title to avoid infinite loops
  const expectedInitialTitle = useMemo(() => {
    if (!formApi.formState?.defaultValues?.formMake || isWaitingForCatalogueLabels) {
      return null;
    }
    return generateVehicleTitle({
      make: formApi.formState?.defaultValues?.formMake,
      model: formApi.formState?.defaultValues?.formModelFamily,
      trim: formApi.formState?.defaultValues?.trim,
      variant: formApi.formState?.defaultValues?.variant,
      makeOptions,
      modelOptions,
    });
  }, [formApi.formState?.defaultValues, makeOptions, modelOptions, isWaitingForCatalogueLabels]); // eslint-disable-line react/preserve-manual-memoization -- Pre-existing violation

  useEffect(
    function syncAutoGeneratedTitle() {
      const currentTitle = formApi.getValues('title');
      const isFieldDirty = formApi.formState.dirtyFields?.title;
      const initialTitle = formApi.formState?.defaultValues?.title;

      // Reset to empty if computed title is empty
      if (isNilOrEmpty(title)) {
        if (isNilOrEmpty(currentTitle)) {
          formApi.resetField('title', {keepDirty: false, keepTouched: false, defaultValue: ''});
        }
        return;
      }

      // Check if initial title was custom (edit mode)
      if (!isNilOrEmpty(initialTitle) && !isFieldDirty) {
        // If the initial title was custom, preserve it
        if (!isAutoGeneratedTitle(initialTitle, expectedInitialTitle)) {
          return; // Preserve custom title
        }
      }

      // Only auto-update if field isn't dirty and title has changed
      if (title && !isFieldDirty && currentTitle !== title) {
        formApi.setValue('title', title, {shouldDirty: false, shouldTouch: false});
      }
    },
    [title, formApi, expectedInitialTitle]
  );

  return (
    <Field
      isRequired
      name="title"
      type="text"
      label={i18n.t('entity.vehicle.labels.vehicleTitle')}
    />
  );
}

/**
 * Memoized TitleField component to prevent unnecessary re-renders
 */
export const TitleField = memo(TitleFieldImpl);
